/**
 * The softphone widget window.
 *
 * First line of defence against two widgets: the window is opened under a FIXED NAME, so a second
 * `window.open` with the same name is handed the existing window instead of creating another. The
 * BroadcastChannel tab lock behind it covers what a name cannot - a second window opened from a
 * different dashboard tab, or one the browser refused to reuse.
 */
export const SOFTPHONE_WINDOW_NAME = 'ringbax-softphone'

export const SOFTPHONE_WINDOW_PATH = '/softphone'

const WIDTH = 380
const HEIGHT = 560

let widgetWindow = null

function buildFeatures() {
  // Centre on the screen the browser is on, not the primary one.
  const left = Math.max(0, Math.round((window.screen?.availWidth || WIDTH) / 2 - WIDTH / 2))
  const top = Math.max(0, Math.round((window.screen?.availHeight || HEIGHT) / 2 - HEIGHT / 2))

  return [
    `width=${WIDTH}`,
    `height=${HEIGHT}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'resizable=yes',
    'scrollbars=yes'
  ].join(',')
}

export function isSoftphoneWindowOpen() {
  return !!widgetWindow && !widgetWindow.closed
}

/**
 * Opens the widget, or focuses it when it is already open.
 *
 * MUST be called straight from a click handler: a popup opened after an await has lost the user
 * gesture and the browser blocks it. That is why the campaigns list opens the window BEFORE it
 * resumes the campaign, rather than after.
 *
 * @returns {Window|null} null when the browser blocked the popup.
 */
export function openSoftphoneWindow() {
  if (isSoftphoneWindowOpen()) {
    widgetWindow.focus()

    return widgetWindow
  }

  const opened = window.open(SOFTPHONE_WINDOW_PATH, SOFTPHONE_WINDOW_NAME, buildFeatures())

  if (!opened) {
    widgetWindow = null

    return null
  }

  widgetWindow = opened
  widgetWindow.focus()

  return widgetWindow
}

/**
 * Calls back once the widget window has gone away.
 *
 * Polling rather than trusting a message from the window itself: a BroadcastChannel post made during
 * `beforeunload` is not reliably delivered, and an agent who closes the widget with the X is the
 * most likely way this ever ends. The tab that opened the window holds the handle, so it can simply
 * watch it.
 *
 * @returns {Function} stop watching.
 */
export function watchSoftphoneWindow(onClosed, intervalInMs = 500) {
  if (!isSoftphoneWindowOpen()) {
    return () => {}
  }

  const watched = widgetWindow

  const intervalId = setInterval(() => {
    if (!watched.closed) {
      return
    }

    clearInterval(intervalId)

    if (widgetWindow === watched) {
      widgetWindow = null
    }

    onClosed()
  }, intervalInMs)

  return () => clearInterval(intervalId)
}

export function closeSoftphoneWindow() {
  if (isSoftphoneWindowOpen()) {
    widgetWindow.close()
  }

  widgetWindow = null
}

// Test seam: the module keeps a handle across calls so a second click focuses rather than reopens.
export const __testing = {
  reset() {
    widgetWindow = null
  },

  getWindow() {
    return widgetWindow
  }
}
